// ============================================================================
// DATA SERVICE - Main orchestration layer
// Combines Google Sheets historical data with API recent data
// ============================================================================

import { fetchGoogleSheetsData } from './googleSheetsService';
import { fetchRecentApiData } from './priceApiService';
import { DATA_CONFIG } from '../config/dataConfig';

/**
 * Fetch complete price data for an asset (historical + recent)
 * @param {string} assetId - Asset identifier (gold, bitcoin, silver)
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Combined array of {date, price} objects
 */
export const fetchAssetPriceData = async (assetId, startDate, endDate) => {
    try {
        console.log(`[DataService] Fetching price data for ${assetId} from ${startDate} to ${endDate}`);

        // Calculate the cutoff date (30 days ago from today)
        const today = new Date();
        const cutoffDate = new Date(today);
        cutoffDate.setDate(cutoffDate.getDate() - DATA_CONFIG.recentDataDays);
        const cutoffDateStr = cutoffDate.toISOString().split('T')[0];

        console.log(`[DataService] Cutoff date: ${cutoffDateStr} (API for dates >= cutoff, Sheets for dates < cutoff)`);

        // Fetch both data sources in parallel
        const [sheetsData, apiData] = await Promise.all([
            fetchGoogleSheetsData(assetId),
            fetchRecentApiData(assetId)
        ]);

        // Filter Google Sheets data: only keep dates BEFORE the cutoff
        const historicalData = sheetsData.filter(item => item.date < cutoffDateStr);

        // Filter API data: only keep dates ON or AFTER the cutoff
        const recentData = apiData.filter(item => item.date >= cutoffDateStr);

        console.log(`[DataService] Historical records (from Sheets): ${historicalData.length}`);
        console.log(`[DataService] Recent records (from API): ${recentData.length}`);

        // Combine the datasets
        let combinedData = [...historicalData, ...recentData];

        // Remove duplicates (prefer API data over Sheets data for same date)
        const dateMap = new Map();

        // First, add historical data
        historicalData.forEach(item => {
            dateMap.set(item.date, item);
        });

        // Then, add/override with recent API data
        recentData.forEach(item => {
            dateMap.set(item.date, item);
        });

        // Convert back to array and sort by date
        combinedData = Array.from(dateMap.values()).sort((a, b) =>
            new Date(a.date) - new Date(b.date)
        );

        // Filter to requested date range
        const filteredData = combinedData.filter(item =>
            item.date >= startDate && item.date <= endDate
        );

        console.log(`[DataService] Total combined records: ${combinedData.length}`);
        console.log(`[DataService] Records in requested range: ${filteredData.length}`);

        // Fill gaps if needed (for weekends/holidays)
        const filledData = fillDataGaps(filteredData, startDate, endDate);

        return filledData;

    } catch (error) {
        console.error(`[DataService] Error fetching data for ${assetId}:`, error);
        throw error;
    }
};

/**
 * Fill gaps in price data by carrying forward the last known price
 * This handles weekends, holidays, and missing data
 */
const fillDataGaps = (data, startDate, endDate) => {
    if (data.length === 0) {
        return data;
    }

    // Create a map of existing dates
    const dataMap = new Map();
    data.forEach(item => {
        dataMap.set(item.date, item.price);
    });

    // Fill gaps
    const filledData = [];
    let currentDate = new Date(startDate);
    const end = new Date(endDate);
    let lastKnownPrice = null;

    while (currentDate <= end) {
        const dateStr = currentDate.toISOString().split('T')[0];

        if (dataMap.has(dateStr)) {
            // We have data for this date
            lastKnownPrice = dataMap.get(dateStr);
            filledData.push({
                date: dateStr,
                price: lastKnownPrice
            });
        } else if (lastKnownPrice !== null) {
            // Fill gap with last known price
            filledData.push({
                date: dateStr,
                price: lastKnownPrice
            });
        }

        currentDate.setDate(currentDate.getDate() + 1);
    }

    return filledData;
};

/**
 * Get data source info for debugging
 */
export const getDataSourceInfo = async (assetId) => {
    const today = new Date();
    const cutoffDate = new Date(today);
    cutoffDate.setDate(cutoffDate.getDate() - DATA_CONFIG.recentDataDays);

    const sheetsData = await fetchGoogleSheetsData(assetId);
    const apiData = await fetchRecentApiData(assetId);

    return {
        assetId,
        cutoffDate: cutoffDate.toISOString().split('T')[0],
        googleSheets: {
            totalRecords: sheetsData.length,
            dateRange: sheetsData.length > 0 ? {
                from: sheetsData[0].date,
                to: sheetsData[sheetsData.length - 1].date
            } : null
        },
        api: {
            totalRecords: apiData.length,
            dateRange: apiData.length > 0 ? {
                from: apiData[0].date,
                to: apiData[apiData.length - 1].date
            } : null
        }
    };
};
